import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Layout,
  Row,
  Col,
  Typography,
  Button,
  Rate,
  Spin,
  message,
  Modal,
  Input,
  Popover
} from 'antd';
import {
  StarOutlined,
  FireOutlined,
  AppstoreOutlined,
  ShareAltOutlined,
  InfoCircleOutlined,
  DownloadOutlined,
  HomeOutlined,
  CloseOutlined,
  TwitterOutlined,
  FacebookOutlined,
  YoutubeOutlined,
  LinkOutlined
} from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Ad from '../../components/Ad/Ad';
import { apiService } from '../../services/api';
import { AppDetail as AppDetailType, AppListItem, Category } from '../../types';
import { constants } from '../../config/constants';
import { trackGAEvent, trackGoogleAdsConversion } from '../../utils/analytics';
import './AppDetail.css';

const { Content } = Layout;
const { Title } = Typography;


const AppDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [app, setApp] = useState<AppDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [ratingModalVisible, setRatingModalVisible] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [userRating, setUserRating] = useState(0);
  const [submittingRating, setSubmittingRating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewVisible, setPreviewVisible] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [popularApps, setPopularApps] = useState<AppListItem[]>([]);
  const [categoryApps, setCategoryApps] = useState<AppListItem[]>([]);
  const [screenshotLoading, setScreenshotLoading] = useState<{ [key: string]: boolean }>({});
  const [currentScreenshotIndex, setCurrentScreenshotIndex] = useState<number>(0);

  useEffect(() => {
    if (id) {
      const appId = parseInt(id);
      trackGoogleAdsConversion('17723879355', 'O9jKCJjLl-UcELvfs4NC', {
        app_id: appId,
      });
      trackGAEvent('App_detail_conv', {
        app_id: appId,
      });
      loadAppDetail(appId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadSidebarData();
  }, []);

  useEffect(() => {
    // 添加键盘事件监听器
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!previewVisible || !app?.screenshots || app.screenshots.length === 0) return;
      
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          const prevIndex = currentScreenshotIndex > 0 ? currentScreenshotIndex - 1 : app.screenshots.length - 1;
          setCurrentScreenshotIndex(prevIndex);
          setPreviewImage(app.screenshots[prevIndex]);
          break;
        case 'ArrowRight':
          e.preventDefault();
          const nextIndex = currentScreenshotIndex < app.screenshots.length - 1 ? currentScreenshotIndex + 1 : 0;
          setCurrentScreenshotIndex(nextIndex);
          setPreviewImage(app.screenshots[nextIndex]);
          break;
        case 'Escape':
          e.preventDefault();
          setPreviewVisible(false);
          break;
      }
    };

    if (previewVisible) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [previewVisible, app?.screenshots, currentScreenshotIndex]);

  const loadAppDetail = async (appId: number) => {
    setLoading(true);
    try {
      const response = await apiService.getAppDetail(appId);
      if (response.code === 0) {
        setApp(response.data);
        // 加载同分类的应用
        loadCategoryApps(response.data.category.id);
      }
    } catch (error) {
      console.error('Failed to load app detail:', error);
      message.error('Load App Detail Failed');
    } finally {
      setLoading(false);
    }
  };

  const loadSidebarData = async () => {
    try {
      // 加载分类列表
      const categoriesResponse = await apiService.getCategories();
      if (categoriesResponse.code === 0) {
        setCategories(categoriesResponse.data);
      }

      // 加载热门应用
      const popularResponse = await apiService.getPopularApps(8);
      if (popularResponse.code === 0) {
        setPopularApps(popularResponse.data);
      }
    } catch (error) {
      console.error('Failed to load sidebar data:', error);
    }
  };

  const loadCategoryApps = async (categoryId: number) => {
    try {
      const response = await apiService.getApps({
        page: 1,
        per_page: constants.pagination.pageSizeOptions[0], // 使用配置中的第一个选项
        category_id: categoryId
      });
      if (response.code === 0) {
        setCategoryApps(response.data.items.filter(item => item.id !== parseInt(id!)));
      }
    } catch (error) {
      console.error('Failed to load category apps:', error);
    }
  };

  const handleRating = async () => {
    if (userRating === 0) {
      message.warning('Please select a rating');
      return;
    }

    setSubmittingRating(true);
    try {
      const response = await apiService.rateApp(parseInt(id!), { rating: userRating });
      if (response.code === 0) {
        message.success('Rating submitted successfully');
        setRatingModalVisible(false);
        setUserRating(0);
        // 重新加载应用详情以获取最新评分
        loadAppDetail(parseInt(id!));
      }
    } catch (error: any) {
      if (error.response?.data?.code === constants.errors.rateLimitCode) {
        message.warning('You have already rated this app');
      } else {
        message.error('Rating failed');
      }
    } finally {
      setSubmittingRating(false);
    }
  };

  const handleDownload = async (platform: 'android' | 'ios') => {
    const downloadUrl = platform === 'android' ? app?.android_download_url : app?.ios_download_url;

    // 检查链接是否为空、null、undefined或只包含空白字符
    if (!downloadUrl || downloadUrl.trim() === '') {
      message.info(`${platform === 'android' ? 'Android' : 'iOS'} download link is not available`);
      return;
    }

    // 增加下载计数
    try {
      await apiService.incrementDownloadCount(parseInt(id!));
    } catch (error) {
      console.error('Failed to increment download count:', error);
      // 不影响下载流程，只记录错误
    }

    // 在新窗口打开下载链接
    window.open(downloadUrl, '_blank');
  };

  const handleSearch = (value: string) => {
    if (value.trim()) {
      window.location.href = `/search/${encodeURIComponent(value.trim())}`;
    }
  };

  const handleRandomPlay = () => {
    if (!app) return;

    // 从当前分类的应用中随机选择一个（排除当前应用）
    const availableApps = categoryApps.filter(gameApp => gameApp.id !== app.id);

    if (availableApps.length > 0) {
      const randomIndex = Math.floor(Math.random() * availableApps.length);
      const randomApp = availableApps[randomIndex];
      window.location.href = `/app/${randomApp.id}`;
    } else {
      // 如果当前分类没有其他应用，从热门应用中随机选择
      const availablePopularApps = popularApps.filter(gameApp => gameApp.id !== app.id);
      if (availablePopularApps.length > 0) {
        const randomIndex = Math.floor(Math.random() * availablePopularApps.length);
        const randomApp = availablePopularApps[randomIndex];
        window.location.href = `/app/${randomApp.id}`;
      }
    }
  };

  // 检查下载链接是否有效的辅助函数
  const isDownloadLinkValid = (url: string | undefined | null): boolean => {
    return !!(url && url.trim() !== '');
  };

  const handleShare = (platform: string) => {
    const shareUrl = window.location.href;
    const shareText = `🎮 Check out this amazing game: ${app?.name}! Play now and discover endless fun! ${shareUrl}`;

    let shareLink = '';
    switch (platform) {
      case 'twitter':
        shareLink = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`;
        break;
      case 'facebook':
        shareLink = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`;
        break;
      case 'youtube':
        // YouTube doesn't have a direct share URL, so we'll open YouTube and let users search for the game
        shareLink = `https://www.youtube.com/results?search_query=${encodeURIComponent(app?.name + ' game')}`;
        break;
      default:
        break;
    }

    if (shareLink) {
      window.open(shareLink, '_blank');
    }
  };

  const copyShareLink = () => {
    const shareUrl = window.location.href;
    const shareText = `🎮 Check out this amazing game: ${app?.name}! Play now and discover endless fun! ${shareUrl}`;

    navigator.clipboard.writeText(shareText).then(() => {
      message.success('Share link copied to clipboard!');
      setShareModalVisible(false); // 复制成功后关闭弹窗
    }).catch(() => {
      message.error('Failed to copy link');
    });
  };

  const handleScreenshotClick = (screenshot: string, index: number) => {
    setPreviewImage(screenshot);
    setCurrentScreenshotIndex(index);
    setPreviewVisible(true);
  };

  const handleScreenshotLoad = (screenshot: string) => {
    setScreenshotLoading(prev => ({ ...prev, [screenshot]: false }));
  };

  const handleScreenshotError = (screenshot: string) => {
    setScreenshotLoading(prev => ({ ...prev, [screenshot]: false }));
    // 可以在这里设置一个默认的占位图片
  };

  const handleScreenshotLoadStart = (screenshot: string) => {
    setScreenshotLoading(prev => ({ ...prev, [screenshot]: true }));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!app?.screenshots || app.screenshots.length === 0) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        const prevIndex = currentScreenshotIndex > 0 ? currentScreenshotIndex - 1 : app.screenshots.length - 1;
        setCurrentScreenshotIndex(prevIndex);
        setPreviewImage(app.screenshots[prevIndex]);
        break;
      case 'ArrowRight':
        e.preventDefault();
        const nextIndex = currentScreenshotIndex < app.screenshots.length - 1 ? currentScreenshotIndex + 1 : 0;
        setCurrentScreenshotIndex(nextIndex);
        setPreviewImage(app.screenshots[nextIndex]);
        break;
      case 'Escape':
        e.preventDefault();
        setPreviewVisible(false);
        break;
    }
  };

  if (loading) {
    return (
      <Layout>
        <Header />
        <Content style={{ padding: '50px', textAlign: 'center' }}>
          <Spin size="large" />
        </Content>
        <Footer />
      </Layout>
    );
  }

  if (!app) {
    return (
      <Layout>
        <Header />
        <Content style={{ padding: '50px', textAlign: 'center' }}>
          <Title level={3}>应用不存在</Title>
          <Button href="/">Home</Button>
        </Content>
        <Footer />
      </Layout>
    );
  }

  return (
    <Layout className="app-detail-layout">
      <Header onSearch={handleSearch} />

      <Content className="app-detail-content">
        <div className="content-container app-detail-container">
          {/* 详情页广告位 */}
          <Ad positionName="detail_page_ad" className="detail-ad" />

          {/* 游戏信息区域 */}
          <section className="game-info-section">
            <div className="game-info-card">
              {/* 游戏名称单独一行 */}
              <div className="game-title-section">
                <div className="title-with-actions">
                  <Title level={2} className="game-title">{app.name}</Title>
                  <div className="title-actions">
                    <Button 
                      type="text" 
                      icon={<HomeOutlined />} 
                      href="/"
                      className="home-button"
                    >
                      Home
                    </Button>
                  </div>
                </div>
              </div>

              {/* 第一行：包含logo和详细信息 */}
              <div className="game-info-row">
                {/* 游戏logo（宽高相同） */}
                <div className="game-logo-section">
                  <img
                    src={app.icon_url || 'https://picsum.photos/id/237/600/800'}
                    alt={app.name}
                    className="game-logo"
                  />
                </div>

                {/* 右侧信息区域：分为左右两列 */}
                <div className="game-info-content">
                  {/* 左列：分类、评分和操作按钮 */}
                  <div className="game-info-left">

                    {/* 开发者信息 */}
                    {app.developer && (
                      <div className="game-developer">
                        <span className="developer-label">Developer: </span>
                        <span className="developer-name">{app.developer}</span>
                      </div>
                    )}

                    {/* 分类标签 */}
                    <div className="game-tags">
                      <span className="tag-category">{app.category.name}</span>
                    </div>

                    {/* 游戏分级信息 - 移至评分上方 */}
                    <div className="game-rating-info">
                      <div className="rating-badge">
                        <InfoCircleOutlined />
                        <span>Content Rating: {app.content_rating || 'Rated for 3+'}</span>
                      </div>
                    </div>

                    {/* 评分 */}
                    <div className="game-rating">
                      <div className="rating-stars">
                        <Rate disabled value={app.rating} allowHalf />
                      </div>
                      <span className="rating-score">{app.rating.toFixed(1)}</span>
                    </div>

                    {/* 操作按钮：带小图标 */}
                    <div className="game-actions">
                      <Button
                        className="btn-rating"
                        icon={<StarOutlined />}
                        onClick={() => setRatingModalVisible(true)}
                      >
                        Rating
                      </Button>
                      <Popover
                        content={
                          <div className="share-modal-content">
                            <div className="share-platforms">
                              <div className="share-platform" onClick={() => handleShare('twitter')}>
                                <div className="share-icon twitter">
                                  <TwitterOutlined />
                                </div>
                              </div>
                              <div className="share-platform" onClick={() => handleShare('facebook')}>
                                <div className="share-icon facebook">
                                  <FacebookOutlined />
                                </div>
                              </div>
                              <div className="share-platform" onClick={() => handleShare('youtube')}>
                                <div className="share-icon youtube">
                                  <YoutubeOutlined />
                                </div>
                              </div>
                              <div className="share-platform" onClick={copyShareLink}>
                                <div className="share-icon link">
                                  <LinkOutlined />
                                </div>
                              </div>
                            </div>
                          </div>
                        }
                        title="Share Game"
                        trigger="click"
                        open={shareModalVisible}
                        onOpenChange={setShareModalVisible}
                        placement="topRight"
                      >
                        <Button
                          className="btn-share"
                          icon={<ShareAltOutlined />}
                        >
                          Share
                        </Button>
                      </Popover>
                    </div>
                  </div>

                  {/* 右列：下载按钮 */}
                  <div className="game-info-right">
                    <Button
                      className="btn-download btn-primary"
                      icon={<DownloadOutlined />}
                      href={`/download/${app.id}`}
                      size="large"
                    >
                      Download
                    </Button>
                  </div>
                </div>
              </div>

              {/* 第二行：游戏简介 */}
              <div className="game-description-section">
                <Title level={4} className="description-title">
                  <InfoCircleOutlined className="description-icon" />
                  Introduction
                </Title>

                {/* 游戏截图 */}
                {app.screenshots && app.screenshots.length > 0 ? (
                  <div className="game-screenshots">
                    <div className="screenshots-container">
                      {app.screenshots.map((screenshot: string, index: number) => (
                        <div
                          key={`${screenshot}-${index}`}
                          className="screenshot-item"
                          onClick={() => handleScreenshotClick(screenshot, index)}
                        >
                          {screenshotLoading[screenshot] && (
                            <div className="screenshot-loading">
                              <Spin size="small" />
                            </div>
                          )}
                          <img
                            src={screenshot}
                            alt={`${app.name} screenshot ${index + 1}`}
                            className="screenshot-image"
                            loading="lazy"
                            onLoadStart={() => handleScreenshotLoadStart(screenshot)}
                            onLoad={() => handleScreenshotLoad(screenshot)}
                            onError={() => handleScreenshotError(screenshot)}
                            style={{ 
                              display: screenshotLoading[screenshot] ? 'none' : 'block' 
                            }}
                          />
                        </div>
                      ))}
                    </div>
                    {app.screenshots.length > 3 && (
                      <div className="screenshots-hint">
                        <span></span>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="no-screenshots">
                    <span></span>
                  </div>
                )}

                <div className="description-content">
                  <ReactMarkdown>{app.description}</ReactMarkdown>
                </div>
              </div>
            </div>
          </section>

          {/* 更多游戏推荐 */}
          <section className="more-games-section">
            <div className="section-header">
              <Title level={4} className="section-title">
                <AppstoreOutlined className="section-icon" />
                More Games
              </Title>
            </div>

            {/* 展示2排游戏 */}
            <Row gutter={[16, 16]} className="games-grid">
              {[...popularApps, ...categoryApps].slice(0, 12).map((gameApp, index) => (
                <Col key={gameApp.id} xs={12} sm={8} md={6} lg={4} xl={4}>
                  <a href={`/app/${gameApp.id}`} className="game-card-link">
                    <div className="game-card game-card-hover">
                      <div className="game-card-image">
                        <img
                          src={gameApp.icon_url || 'https://picsum.photos/300/300'}
                          alt={gameApp.name}
                          className="game-card-img"
                        />
                      </div>
                      <div className="game-card-info">
                        <Title level={5} className="game-card-title" ellipsis>
                          {gameApp.name}
                        </Title>
                        <div className="game-card-rating">
                          <Rate disabled value={4 + Math.random()} allowHalf style={{ fontSize: '12px' }} />
                          <span className="rating-text">{(4 + Math.random()).toFixed(1)}</span>
                        </div>
                      </div>
                    </div>
                  </a>
                </Col>
              ))}
            </Row>

            {/* 随机玩按钮 */}
            <div className="random-play-section">
              <Button
                type="primary"
                size="large"
                className="btn-random-play"
                icon={<FireOutlined />}
                onClick={handleRandomPlay}
              >
                Random Play
              </Button>
            </div>
          </section>
        </div>
      </Content>

      {/* 评分弹窗 */}
      <Modal
        title={
          <div className="rating-modal-header">
            <Button
              type="text"
              onClick={() => setRatingModalVisible(false)}
              className="close-btn"
            />
          </div>
        }
        open={ratingModalVisible}
        onCancel={() => setRatingModalVisible(false)}
        footer={null}
        width={400}
        className="rating-modal"
      >
        <div className="rating-modal-content">
          <div className="game-info-modal">
            <img
              src={app.icon_url || 'https://picsum.photos/id/237/200/200'}
              alt={app.name}
              className="game-logo-modal"
            />
            <Title level={4} className="game-title-modal">{app.name}</Title>
          </div>
          
          <div className="rating-stars-modal">
            <Rate
              value={userRating}
              onChange={setUserRating}
              className="rating-stars-large"
            />
          </div>
          
          <Button
            type="primary"
            size="large"
            onClick={handleRating}
            loading={submittingRating}
            className="submit-rating-btn"
            disabled={userRating === 0}
          >
            Submit Rating
          </Button>
        </div>
      </Modal>

      {/* 截图预览Modal */}
      <Modal
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={null}
        width="80%"
        style={{ maxWidth: '800px' }}
        className="screenshot-preview-modal"
        centered
        destroyOnClose
      >
        <div className="screenshot-preview-content">
          <div className="screenshot-preview-header">
            <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={() => setPreviewVisible(false)}
              className="screenshot-close-btn"
            />
          </div>
          
          {app?.screenshots && app.screenshots.length > 1 && (
            <div className="screenshot-navigation">
              <Button
                type="text"
                icon={<CloseOutlined style={{ transform: 'rotate(180deg)' }} />}
                onClick={() => {
                  const prevIndex = currentScreenshotIndex > 0 ? currentScreenshotIndex - 1 : app.screenshots.length - 1;
                  setCurrentScreenshotIndex(prevIndex);
                  setPreviewImage(app.screenshots[prevIndex]);
                }}
                className="screenshot-nav-btn screenshot-nav-prev"
                disabled={app.screenshots.length <= 1}
              />
              <span className="screenshot-counter">
                {currentScreenshotIndex + 1} / {app.screenshots.length}
              </span>
              <Button
                type="text"
                icon={<CloseOutlined />}
                onClick={() => {
                  const nextIndex = currentScreenshotIndex < app.screenshots.length - 1 ? currentScreenshotIndex + 1 : 0;
                  setCurrentScreenshotIndex(nextIndex);
                  setPreviewImage(app.screenshots[nextIndex]);
                }}
                className="screenshot-nav-btn screenshot-nav-next"
                disabled={app.screenshots.length <= 1}
              />
            </div>
          )}
          
          <div className="screenshot-preview-image-container">
            <img
              src={previewImage}
              alt="Screenshot preview"
              className="screenshot-preview-image"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                const errorDiv = document.createElement('div');
                errorDiv.className = 'screenshot-preview-error';
                errorDiv.innerHTML = '<span>图片加载失败</span>';
                target.parentNode?.appendChild(errorDiv);
              }}
            />
          </div>
        </div>
      </Modal>

      <Footer />
    </Layout>
  );
};

export default AppDetail;
